import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { f as PropTypes, H as HamburgerButton, a as Logo, N as NavigationLink, e as SourceIdLink, S as SearchIcon, d as Skeleton, T as TextField, c as OfferLogo, P as ProviderHighlights, O as OfferCriteria, b as OfferCta } from "./OfferCta-OU6Yjw7w.js";
import { useContext, useState, useEffect } from "react";
import { G as GlobalHeadContext, g as getEnvVariable } from "./index-C9TrZnbr.js";
import { BREAKPOINT_DIMENSIONS } from "@quickenloans-ui-suite/design-system";
import { N as NAVIGATION_BAR_ACTIONS, D as DRAWER_TRANSITION_DURATION_DESKTOP, a as DRAWER_TRANSITION_DURATION, A as ARIA_LABELS, L as LABELS, T as TOTAL_SKELETON_LOADER_CARDS, M as MKT_CONTAINER_ID } from "./index-BIblv54e.js";
import { Box, AppBar, Slide, Toolbar, Drawer, Divider, Typography } from "@mui/material";
import { c as clearBodyNoScroll, g as getDrawerProps } from "./drawer-BfLuYH0y.js";
import { u as useCustomScrollTrigger, a as useSearchBar } from "./useCustomScrollTrigger-DD3As7oF.js";
import { styled } from "@mui/system";
import { LogoutOutlined, NavigateBefore, KeyboardArrowUpOutlined, KeyboardArrowDownOutlined, Search } from "@mui/icons-material";
import { Headline5, Button, BYOButton } from "@cdm/quickenloans-library/lib/";
import { styled as styled$1 } from "@mui/material/styles";
import { c as mockSkeletonSearchCTA, b as mockSkeletonNavBar, m as mockSkeletonMarketplace } from "./mockSkeletons-Dzu6jNn4.js";
import ErrorIcon from "@mui/icons-material/Error";
const ribbonHeight = {
  desktop: "88px",
  mobile: "70px"
};
const getHeight = (isDesktopLayout) => isDesktopLayout ? ribbonHeight.desktop : ribbonHeight.mobile;
const StyledRibbonContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isClient" && prop !== "isDesktopLayout"
})(({ theme, isClient, isDesktopLayout }) => {
  var _a, _b;
  return {
    position: "fixed",
    width: "100vw",
    top: 0,
    zIndex: ((_b = (_a = theme.vars) == null ? void 0 : _a.zIndex) == null ? void 0 : _b.ribbon) ?? 1250,
    visibility: isClient ? "visible" : "hidden",
    minHeight: getHeight(isDesktopLayout),
    "& .header-strip-container-desktop": {
      [theme.breakpoints.down(theme.breakpoints.values.xl)]: {
        visibility: "hidden"
      }
    },
    "& .header-strip-container-mobile": {
      [theme.breakpoints.up(theme.breakpoints.values.xl)]: {
        visibility: "hidden"
      }
    }
  };
});
const StyledMobileAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "isDrawerOpen" && prop !== "isSearchBarOpen"
})(({ theme, isDrawerOpen, isSearchBarOpen }) => {
  var _a, _b, _c;
  return {
    padding: "20px 16px",
    backgroundColor: (_a = theme.vars.palette.grey) == null ? void 0 : _a[0],
    color: "black",
    boxShadow: (_b = theme.vars.shadows) == null ? void 0 : _b[2],
    position: "relative",
    zIndex: `calc(11 + ${theme.vars.zIndex.drawer})`,
    borderBottom: isDrawerOpen ? `1px solid ${(_c = theme.vars.palette.grey) == null ? void 0 : _c[20]}` : "none",
    boxShadow: isSearchBarOpen ? "none" : `${theme.vars.shadows[2]}`,
    "& .MuiToolbar-root": {
      boxSizing: "border-box",
      display: "flex",
      gap: "16px",
      minHeight: "30px",
      maxHeight: "30px",
      padding: "0px",
      maxWidth: "100%"
    }
  };
});
const StyledDesktopAppBar = styled(AppBar)(
  ({ theme }) => {
    var _a, _b;
    const { xl, xxl } = theme.breakpoints.contentGrids;
    return {
      padding: "20px 0px",
      backgroundColor: (_a = theme.vars.palette.grey) == null ? void 0 : _a[0],
      color: "black",
      boxShadow: (_b = theme.vars.shadows) == null ? void 0 : _b[2],
      position: "relative",
      zIndex: `calc(11 + ${theme.vars.zIndex.drawer})`,
      maxHeight: ribbonHeight.desktop,
      "& .MuiToolbar-root": {
        boxSizing: "border-box",
        display: "flex",
        gap: "16px",
        justifyContent: "space-between",
        minHeight: "40px",
        padding: "0px",
        width: "100%",
        margin: "0 auto",
        [theme.breakpoints.up(theme.breakpoints.values.xl)]: {
          maxWidth: xl.columns.full
        },
        [theme.breakpoints.up(theme.breakpoints.values.xxl)]: {
          maxWidth: xxl.columns.full,
          gap: "24px"
        }
      }
    };
  }
);
const StyledContainer = styled(Box)({
  display: "flex"
});
const MobileRibbonLayout = ({
  ribbonRef
}) => {
  const {
    navigationLinkNodes,
    isTierOneNavOpen,
    isTierTwoNavOpen,
    isSearchBarOpen,
    handleNavBarClick
  } = useContext(GlobalHeadContext);
  const { trigger, enableSystemScroll } = useCustomScrollTrigger();
  const [tier2Nodes, setTier2Nodes] = useState([]);
  const [tier2MenuTitle, setTier2MenuTitle] = useState("");
  const [lastScrollY, setLastScrollY] = useState(0);
  const isDrawerOpen = isTierOneNavOpen || isTierTwoNavOpen;
  useEffect(() => clearBodyNoScroll);
  useEffect(() => {
    ribbonRef.current.style.pointerEvents = trigger ? "" : "none";
  }, [trigger]);
  const handleHamburgerClick = () => {
    if (isDrawerOpen) {
      enableSystemScroll(lastScrollY);
      handleNavBarClick(NAVIGATION_BAR_ACTIONS.CLOSE_ALL);
      return;
    }
    if (window) {
      setLastScrollY(window.scrollY);
    }
    handleNavBarClick(NAVIGATION_BAR_ACTIONS.TIER_1, true);
  };
  const handleOpenTier2 = (node) => {
    var _a;
    setLastScrollY(lastScrollY);
    setTier2Nodes(((_a = node == null ? void 0 : node.childItems) == null ? void 0 : _a.nodes) ?? []);
    setTier2MenuTitle(node == null ? void 0 : node.label);
    handleNavBarClick(NAVIGATION_BAR_ACTIONS.TIER_2, true);
  };
  const handleGoBackToTier1 = () => {
    handleNavBarClick(NAVIGATION_BAR_ACTIONS.TIER_1, true);
  };
  const navBarProps = {
    onClose: () => {
      handleNavBarClick(NAVIGATION_BAR_ACTIONS.CLOSE_ALL);
      enableSystemScroll(lastScrollY);
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Slide, { appear: false, direction: "down", in: isDrawerOpen || trigger, children: /* @__PURE__ */ jsx(
      StyledMobileAppBar,
      {
        "data-testid": "header-bar",
        isDrawerOpen,
        isSearchBarOpen,
        children: /* @__PURE__ */ jsxs(Toolbar, { className: "header-strip-container-mobile", "data-testid": "mobile-header-strip-container", children: [
          /* @__PURE__ */ jsx(StyledContainer, { "data-testid": "mobile-menu-icons", children: /* @__PURE__ */ jsx(
            HamburgerButton,
            {
              isOpen: isDrawerOpen,
              onClick: handleHamburgerClick
            }
          ) }),
          /* @__PURE__ */ jsx(StyledContainer, { id: "mobile-ql-logo", "data-testid": "mobile-ql-logo", children: /* @__PURE__ */ jsx(Logo, {}) }),
          /* @__PURE__ */ jsx(StyledContainer, { "data-testid": "mobile-cta-and-search", style: { marginLeft: "auto" }, children: /* @__PURE__ */ jsx(
            SearchCTA,
            {
              isDesktop: false,
              ribbonRef,
              onOpenSearchBar: () => setLastScrollY(window.scrollY),
              onCloseSearchBar: () => () => enableSystemScroll(lastScrollY)
            }
          ) })
        ] })
      }
    ) }),
    /* @__PURE__ */ jsx(
      NavigationBarMobile,
      {
        isVisible: isTierOneNavOpen,
        navigationLinkNodes,
        isTier2: false,
        navBarProps,
        ribbonParentRef: ribbonRef,
        handleClickOnSubNavLink: (node) => handleOpenTier2(node)
      }
    ),
    /* @__PURE__ */ jsx(
      NavigationBarMobile,
      {
        isVisible: isTierTwoNavOpen,
        navigationLinkNodes: tier2Nodes,
        isTier2: true,
        navBarProps,
        ribbonParentRef: ribbonRef,
        handleGoBackToTier1,
        menuTitle: tier2MenuTitle
      }
    )
  ] });
};
MobileRibbonLayout.propTypes = {
  ribbonRef: PropTypes.object.isRequired
};
const StyledMenuDrawer = ({ isAccountMenu, children, ...rest }) => /* @__PURE__ */ jsx(Drawer, { ...rest, children });
const StyledDrawer$2 = styled(StyledMenuDrawer)(
  ({ theme, isAccountMenu }) => {
    return `
            display: none;
            ${theme.breakpoints.up(BREAKPOINT_DIMENSIONS.extra_large)} {
                display: contents;
            }
            .MuiPaper-root {
                width: 100%;
                height: ${isAccountMenu ? "407px" : "auto"};
                padding: 4px 0 ${isAccountMenu ? 20 : 36}px;
                box-shadow: ${theme.vars.shadows[2]};
            }
            .EndemicContentContainer {
                height: 355px; 
                width: 227px;
                display: flex;
                justify-content: center;
                align-items: center;
                text-align: center;
                flex-direction: column;
                h5 {
                    margin-top: 11px;
                }
                img {
                    width: 140px;
                }
            }
        `;
  }
);
const StyledDrawerBox = ({ isAccountMenu, children, ...rest }) => /* @__PURE__ */ jsx("nav", { itemScope: !isAccountMenu ? true : null, itemType: !isAccountMenu ? "https://schema.org/SiteNavigationElement" : null, ...rest, children });
const StyledBox = styled(StyledDrawerBox)(
  ({ theme, isAccountMenu }) => {
    const { link } = theme["global-head"].nav.desktop;
    const xl = theme.breakpoints.contentGrids[BREAKPOINT_DIMENSIONS.extra_large];
    const xxl = theme.breakpoints.contentGrids[BREAKPOINT_DIMENSIONS.extra_extra_large];
    return `
            ${theme.breakpoints.up(BREAKPOINT_DIMENSIONS.extra_large)} {
                display: flex;
                flex-direction: column;
                flex-wrap: wrap;
                column-gap: ${xl.columns.gutter};
                align-items: flex-start;
                place-content: flex-start;
                max-height: calc(6 * (${link.height}px + ${isAccountMenu ? 1.6 : 1}rem));
                width: ${xl.columns.full};
                text-align: left;
                margin: 0 auto;
                animation: navBoxReveal 600ms ease-out forwards;

                @keyframes navBoxReveal {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
            }
            ${theme.breakpoints.up(BREAKPOINT_DIMENSIONS.extra_extra_large)} {
                width: ${xxl.columns.full};
                column-gap: ${xxl.columns.gutter};
            }
        `;
  }
);
const StyledNavContainer = styled("div")(
  ({ theme }) => {
    const xl = theme.breakpoints.contentGrids[BREAKPOINT_DIMENSIONS.extra_large];
    const xxl = theme.breakpoints.contentGrids[BREAKPOINT_DIMENSIONS.extra_extra_large];
    return `
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            width: ${xl.columns[3]};
            ${theme.breakpoints.up(BREAKPOINT_DIMENSIONS.extra_extra_large)} {
                width: ${xxl.columns[3]};
            }
        `;
  }
);
const StyledNavigationLink = styled(NavigationLink)(
  ({ theme }) => {
    return `
        width: auto;    
        margin-bottom: 1rem;
        `;
  }
);
const classNames = {
  NAV_LINK_CONTAINER: "nav-link-container",
  NAV_BAR_DESKTOP: "nav-bar-desktop"
};
const testIds = {
  ...classNames
};
const renderLink = (node) => {
  var _a;
  return /* @__PURE__ */ jsx(
    StyledNavigationLink,
    {
      title: node.label,
      href: node.url,
      isForMainNav: false,
      icon: ((_a = node.url) == null ? void 0 : _a.includes("/logout")) ? /* @__PURE__ */ jsx(LogoutOutlined, {}) : null
    },
    `grpNav${node.databaseId}`
  );
};
const renderContainer = (containedLinks, containerKey) => /* @__PURE__ */ jsx(
  StyledNavContainer,
  {
    className: classNames.NAV_LINK_CONTAINER,
    "data-testid": testIds.NAV_LINK_CONTAINER,
    children: containedLinks
  },
  containerKey
);
const MegaMenuNavLinks = ({ navigationLinkNodes }) => {
  const nodes = [...navigationLinkNodes];
  let navLinkContainers = [];
  const linksModulo = navigationLinkNodes.length % 6;
  const hasOrphan = linksModulo === 1;
  const singleIndex = navigationLinkNodes.length - 7;
  for (let ix = 0; ix < nodes.length; ix++) {
    if (hasOrphan && ix === singleIndex) {
      navLinkContainers.push(renderContainer(
        [renderLink(nodes[ix])],
        "ctnNavSingleElement"
      ));
    } else {
      const containedNodes = nodes.slice(ix, ix + 2);
      ix++;
      const containedLinks = [];
      containedNodes.forEach((node) => {
        containedLinks.push(renderLink(node));
      });
      navLinkContainers.push(renderContainer(
        containedLinks,
        containedNodes.reduce((key, node) => `${key},${node.databaseId}`, "ctnNav")
      ));
    }
  }
  return /* @__PURE__ */ jsx(Fragment, { children: navLinkContainers });
};
MegaMenuNavLinks.propTypes = {
  navigationLinkNodes: PropTypes.arrayOf(PropTypes.object)
};
const EndemicContent = ({
  dataTestId,
  userName
}) => {
  return /* @__PURE__ */ jsxs(Box, { className: "EndemicContentContainer", children: [
    /* @__PURE__ */ jsx(
      "img",
      {
        src: "https://content.quickenloans.com/ql/brand/illustrations/Standing_-_Green.svg",
        alt: "Endemic Content Illustration",
        title: "Endemic Content Illustration",
        role: "img",
        "data-testid": `${dataTestId}-endemic-content-illustration`
      }
    ),
    /* @__PURE__ */ jsxs(Headline5, { "data-testid": `${dataTestId}-endemic-content-welcome-message`, className: "h5_bold", children: [
      "Welcome,",
      /* @__PURE__ */ jsx("br", {}),
      userName,
      "!"
    ] })
  ] });
};
EndemicContent.propTypes = {
  dataTestId: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired
};
const MegaMenu = ({
  isVisible,
  navigationLinkNodes = [],
  sx = {},
  navBarProps = {},
  ribbonParentRef,
  // @NOTE: ref of parent container for ribbon, should not have extra vert. height
  isAccountMenu = false,
  userName
}) => {
  const {
    paperProps,
    backdropProps,
    slideProps,
    modalProps
  } = getDrawerProps({
    ribbonParentRef,
    dataTestIdBackdrop: "nav-bar-backdrop"
  });
  const conditionalModalProps = {
    ...modalProps,
    keepMounted: !isAccountMenu
  };
  return /* @__PURE__ */ jsx(
    StyledDrawer$2,
    {
      open: isVisible,
      anchor: "top",
      transitionDuration: DRAWER_TRANSITION_DURATION_DESKTOP,
      sx,
      PaperProps: paperProps,
      BackdropProps: backdropProps,
      SlideProps: slideProps,
      ModalProps: conditionalModalProps,
      variant: "temporary",
      ...navBarProps,
      className: classNames.NAV_BAR_DESKTOP,
      "data-testid": testIds.NAV_BAR_DESKTOP,
      elevation: 5,
      isAccountMenu,
      children: /* @__PURE__ */ jsxs(StyledBox, { "data-testid": "nav-styled-box", isAccountMenu, children: [
        isAccountMenu && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(EndemicContent, { dataTestId: "mega-menu", userName }),
          /* @__PURE__ */ jsx(Divider, { orientation: "vertical" })
        ] }),
        /* @__PURE__ */ jsx(MegaMenuNavLinks, { navigationLinkNodes })
      ] })
    }
  );
};
MegaMenu.propTypes = {
  isVisible: PropTypes.bool,
  navigationLinkNodes: PropTypes.arrayOf(PropTypes.object),
  sx: PropTypes.object,
  navBarProps: PropTypes.object,
  ribbonParentRef: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func
  ]),
  isAccountMenu: PropTypes.bool,
  userName: PropTypes.string
};
const StyledDesktopNavBar = styled$1(Box)({
  alignItems: "center",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "row",
  gap: "16px",
  justifyContent: "left",
  maxHeight: "37px",
  maxWidth: "742px",
  minWidth: "696px"
});
const StyledDrawer$1 = styled$1(Drawer)(({ theme }) => ({
  display: "contents",
  "& .MuiBackdrop-root": {
    zIndex: 0
  },
  "& .MuiPaper-root": {
    width: "287px",
    [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.medium)]: {
      width: "352px",
      padding: "24px"
    },
    overflow: "auto",
    height: "100%",
    padding: "24px 8px",
    boxShadow: "none"
  }
}));
const Tier1Container = styled$1(Box)(({ theme }) => {
  var _a;
  return {
    "& .ButtonContainer": {
      display: "flex",
      justifyContent: "center",
      paddingTop: "8px",
      "& a": {
        height: "48px",
        minWidth: "255px",
        [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.medium)]: {
          minWidth: "285px"
        }
      }
    },
    "& .EndemicContentContainer": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
      flexDirection: "column",
      "& img": {
        width: "79px"
      },
      "& h5": {
        margin: "8px 0px 16px"
      }
    },
    "& .MuiDivider-root": {
      marginBottom: "24px",
      borderColor: (_a = theme.vars.palette.grey) == null ? void 0 : _a[20]
    }
  };
});
const Tier2Container = styled$1(Box)(({ theme }) => {
  var _a, _b;
  return {
    "& .MuiToolbar-root": {
      display: "flex",
      padding: "0px 0px 16px",
      minHeight: "24px",
      cursor: "pointer",
      WebkitTapHighlightColor: "transparent",
      // Remove light blue hightlight on toolbar click 
      "& .MuiTypography-root": {
        textAlign: "center",
        flexGrow: "1",
        color: (_a = theme.vars.palette.grey) == null ? void 0 : _a[80],
        fontWeight: "600"
      }
    },
    "& .MuiDivider-root": {
      marginBottom: "24px",
      borderColor: (_b = theme.vars.palette.grey) == null ? void 0 : _b[20]
    }
  };
});
const IconContainer = styled$1(Box)(({ theme }) => {
  var _a;
  return {
    width: "24px",
    height: "24px",
    svg: {
      fill: (_a = theme.vars.palette.grey) == null ? void 0 : _a[80],
      width: "100%",
      height: "100%"
    }
  };
});
const NavigationBarMobile = ({
  isVisible,
  navigationLinkNodes = [],
  isTier2 = false,
  menuTitle = "",
  // Applies to Tier 2
  handleClickOnSubNavLink = (node) => {
  },
  handleGoBackToTier1 = () => {
  },
  // Applies to Tier 2
  sx = {},
  navBarProps = {},
  ribbonParentRef
  // @NOTE: ref of parent container for ribbon, should not have extra vert. height
}) => {
  const {
    environment: currentEnv,
    globalAccountLinks,
    isAuthenticated,
    userName
  } = useContext(GlobalHeadContext);
  const sourceIdHref = getEnvVariable({
    currentEnv,
    keyToSearch: "REFINANCE_BASE_URL"
  });
  const {
    paperProps,
    backdropProps,
    slideProps,
    modalProps
  } = getDrawerProps({
    ribbonParentRef,
    dataTestIdPaper: "nav-bar-mobile-paper",
    dataTestIdBackdrop: "nav-bar-mobile-backdrop",
    isMobile: true
  });
  const MyAccountMenuNode = {
    databaseId: 1158,
    label: "My Account",
    path: "#",
    url: "#",
    parentDatabaseId: 0,
    childItems: {
      nodes: globalAccountLinks
    }
  };
  const renderNavigationLinks = (navLinks, hasTier2 = false) => {
    return navLinks.map((node) => {
      var _a, _b, _c;
      const hasChildren = ((_b = (_a = node == null ? void 0 : node.childItems) == null ? void 0 : _a.nodes) == null ? void 0 : _b.length) > 0 || hasTier2;
      return /* @__PURE__ */ jsx(
        NavigationLink,
        {
          title: node.label,
          href: !hasChildren ? node.url : null,
          hasChildren,
          sx: { marginBottom: "16px" },
          isForMainNav: false,
          onClick: () => {
            if (hasChildren) {
              handleClickOnSubNavLink(node);
            }
          },
          icon: ((_c = node.url) == null ? void 0 : _c.includes("/logout")) ? /* @__PURE__ */ jsx(LogoutOutlined, {}) : null
        },
        `${isTier2 ? "tier-2" : "tier-1"}-${node.databaseId}`
      );
    });
  };
  const mobileModalProps = {
    ...modalProps,
    keepMounted: false
  };
  return /* @__PURE__ */ jsx(
    StyledDrawer$1,
    {
      open: isVisible,
      sx,
      ...navBarProps,
      "data-testid": "nav-bar-mobile",
      PaperProps: paperProps,
      BackdropProps: backdropProps,
      SlideProps: slideProps,
      ModalProps: mobileModalProps,
      transitionDuration: DRAWER_TRANSITION_DURATION,
      elevation: 5,
      children: isTier2 ? /* @__PURE__ */ jsxs(Tier2Container, { children: [
        /* @__PURE__ */ jsxs(
          Toolbar,
          {
            id: "nav-bar-mobile-header",
            onClick: handleGoBackToTier1,
            onKeyDown: (event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleGoBackToTier1();
              }
            },
            children: [
              /* @__PURE__ */ jsx(IconContainer, { "data-testid": "nav-bar-mobile-header-icon", children: /* @__PURE__ */ jsx(NavigateBefore, {}) }),
              /* @__PURE__ */ jsx(Typography, { "data-testid": "nav-bar-mobile-header-text", children: menuTitle }),
              /* @__PURE__ */ jsx(IconContainer, { children: /* @__PURE__ */ jsx(NavigateBefore, { style: { visibility: "hidden" } }) })
            ]
          }
        ),
        /* @__PURE__ */ jsx(Divider, {}),
        /* @__PURE__ */ jsx(Box, { children: renderNavigationLinks(navigationLinkNodes) })
      ] }) : /* @__PURE__ */ jsxs(Tier1Container, { children: [
        isAuthenticated && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(EndemicContent, { dataTestId: "nav-bar-mobile", userName }),
          renderNavigationLinks([MyAccountMenuNode], true),
          /* @__PURE__ */ jsx(Divider, {})
        ] }),
        renderNavigationLinks(navigationLinkNodes),
        /* @__PURE__ */ jsx(Box, { className: "ButtonContainer", children: /* @__PURE__ */ jsx(
          SourceIdLink,
          {
            href: sourceIdHref,
            target: "_blank",
            "aria-label": ARIA_LABELS.GET_STARTED,
            "data-testid": "nav-bar-mobile-get-started-button",
            children: LABELS.GET_STARTED
          }
        ) })
      ] })
    }
  );
};
NavigationBarMobile.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  navigationLinkNodes: PropTypes.arrayOf(PropTypes.object),
  isTier2: PropTypes.bool,
  menuTitle: PropTypes.string,
  handleClickOnSubNavLink: PropTypes.func,
  handleGoBackToTier1: PropTypes.func,
  sx: PropTypes.object,
  navBarProps: PropTypes.object
};
const NavigationBarDesktop = ({
  isVisible = false,
  navLinkSx = {},
  ribbonRef = {},
  sx = {}
}) => {
  if (!isVisible) {
    return null;
  }
  const {
    handleMyAccountMenuClick,
    handleNavBarClick,
    isDesktop,
    isMenuLoading,
    isTierOneNavOpen,
    navigationLinkNodes
  } = useContext(GlobalHeadContext);
  const [navigationLinkOpen, setNavigationLinkOpen] = useState(-1);
  const handleClick = (event, databaseId) => {
    var _a, _b;
    if (((_b = (_a = event == null ? void 0 : event.target) == null ? void 0 : _a.tagName) == null ? void 0 : _b.toLowerCase()) === "a") {
      event.preventDefault();
      setNavigationLinkOpen(-1);
      return;
    }
    const isSameLink = databaseId === navigationLinkOpen;
    setNavigationLinkOpen(isSameLink ? -1 : databaseId);
    handleNavBarClick(NAVIGATION_BAR_ACTIONS.TIER_1, !isSameLink);
  };
  const navBarProps = {
    onClose: () => {
      setNavigationLinkOpen(-1);
      handleNavBarClick(NAVIGATION_BAR_ACTIONS.TIER_1, false);
    }
  };
  const shouldRenderSkeleton = isMenuLoading || !isMenuLoading && !navigationLinkNodes.length;
  useEffect(() => {
    if (!isTierOneNavOpen && navigationLinkOpen !== -1) {
      setNavigationLinkOpen(-1);
    }
  }, [isTierOneNavOpen, navigationLinkOpen]);
  useEffect(() => {
    if (!isDesktop) {
      navBarProps.onClose();
      handleMyAccountMenuClick(false);
    }
  }, [isDesktop]);
  return /* @__PURE__ */ jsx(StyledDesktopNavBar, { "data-testid": "desktop-nav-bar", sx, itemScope: true, itemType: "https://schema.org/SiteNavigationElement", children: shouldRenderSkeleton ? /* @__PURE__ */ jsx(SkeletonNavBarDesktop, {}) : navigationLinkNodes.map(({ childItems, databaseId, label, url }) => {
    var _a;
    const hasChildren = childItems && ((_a = childItems == null ? void 0 : childItems.nodes) == null ? void 0 : _a.length);
    const isNavigationLinkActive = isTierOneNavOpen && navigationLinkOpen === databaseId;
    return /* @__PURE__ */ jsxs("div", { "data-testid": `desktop-nav-bar-megamenu-container-${databaseId}`, children: [
      /* @__PURE__ */ jsx(
        NavigationLink,
        {
          href: hasChildren ? "#" : url,
          isActive: isNavigationLinkActive,
          sx: navLinkSx,
          title: label,
          onClick: (event) => handleClick(event, databaseId)
        }
      ),
      // Validation added to prevent unwanted behavior when clicking anchor tags
      isDesktop && hasChildren ? /* @__PURE__ */ jsx(
        MegaMenu,
        {
          isVisible: isNavigationLinkActive,
          navBarProps,
          navigationLinkNodes: (childItems == null ? void 0 : childItems.nodes) ?? [],
          ribbonParentRef: ribbonRef
        }
      ) : null
    ] }, databaseId);
  }) });
};
NavigationBarDesktop.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  navLinkSx: PropTypes.object,
  ribbonRef: PropTypes.object,
  sx: PropTypes.object
};
const fontWeightSemibold = "var(--font-weight-semibold)", focusBgColor = "var(--color-primary-110)";
const SearchCTAContainer = styled(Box)(
  ({ theme }) => {
    return {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      [theme.breakpoints.up("xxl")]: {
        gap: "16px"
      },
      "& .get-started-button": {
        height: "48px",
        width: "144px"
      }
    };
  }
);
const AccountButton = styled(Button)(
  ({ theme }) => {
    var _a;
    return {
      cursor: "pointer",
      fontFamily: theme.typography.fontFamily,
      fontWeight: fontWeightSemibold,
      border: 0,
      "&:focus": {
        "& svg": {
          transform: "rotate(-180deg)"
        }
      },
      "&.active": {
        backgroundColor: (_a = theme.vars.palette.primary) == null ? void 0 : _a[10],
        color: focusBgColor,
        "& svg": {
          fill: focusBgColor
        }
      },
      "& svg": {
        transition: "transform 0.3s ease"
      },
      "&:focus-visible": {
        outline: "auto 1px black"
      }
    };
  }
);
const SignInButton = styled(BYOButton)(
  ({ theme }) => {
    var _a, _b;
    return {
      cursor: "pointer",
      fontFamily: theme.typography.fontFamily,
      fontWeight: fontWeightSemibold,
      height: "48px",
      border: 0,
      "&:focus-visible": {
        outline: "auto 1px black"
      },
      "&:hover": {
        // Prevent error icon from turning blue on button hover
        ".MuiSvgIcon-root": {
          fill: (_a = theme.vars.palette.warning) == null ? void 0 : _a[90]
        }
      },
      ".MuiSvgIcon-root": {
        fill: (_b = theme.vars.palette.warning) == null ? void 0 : _b[90]
      }
    };
  }
);
const SearchCTA = ({
  isDesktop,
  ribbonRef = {},
  onOpenSearchBar = () => {
  },
  onCloseSearchBar = () => {
  }
}) => {
  const {
    environment: currentEnv,
    globalAccountLinks,
    handleMyAccountMenuClick,
    handleSearchBarClick,
    isAuthenticated,
    isMyAccountMenuOpen,
    isSearchBarOpen,
    isUserDataLoading,
    userDataError,
    userName
  } = useContext(GlobalHeadContext);
  const sourceIdHref = getEnvVariable({
    currentEnv,
    keyToSearch: "REFINANCE_BASE_URL"
  });
  const signInHref = getEnvVariable({
    currentEnv,
    keyToSearch: "AUTH0_LOGIN_URL"
  });
  const searchBarProps = {
    onClose: () => {
      handleSearchBarClick(false);
      onCloseSearchBar();
      setTimeout(() => {
        document.getElementById("SearchIconButton").focus();
      }, 100);
    }
  };
  const myAccountMenuProps = {
    onClose: () => {
      handleMyAccountMenuClick(false);
      setTimeout(() => {
        document.getElementById("search-cta-account-button").focus();
      }, 100);
    }
  };
  const SourceIdButton = () => /* @__PURE__ */ jsx(
    SourceIdLink,
    {
      className: "get-started-button",
      href: sourceIdHref,
      target: "_blank",
      "aria-label": ARIA_LABELS.GET_STARTED,
      "data-testid": "search-cta-get-started-button",
      id: "search-cta-get-started-button",
      children: LABELS.GET_STARTED
    }
  );
  const DesktopCTAButtonState = () => {
    if (isUserDataLoading) {
      return /* @__PURE__ */ jsx(SkeletonSearchCTADesktop, {});
    }
    if (isAuthenticated) {
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          AccountButton,
          {
            style: "outlined",
            color: "blue",
            onClick: () => handleMyAccountMenuClick(!isMyAccountMenuOpen),
            "data-testid": "search-cta-account-button",
            id: "search-cta-account-button",
            iconRight: isMyAccountMenuOpen ? /* @__PURE__ */ jsx(KeyboardArrowUpOutlined, {}) : /* @__PURE__ */ jsx(KeyboardArrowDownOutlined, {}),
            "aria-label": ARIA_LABELS.MY_ACCOUNT,
            role: "button",
            className: isMyAccountMenuOpen ? "active" : "",
            children: LABELS.MY_ACCOUNT
          }
        ),
        /* @__PURE__ */ jsx(SourceIdButton, {})
      ] });
    }
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        SignInButton,
        {
          style: "outlined",
          color: "blue",
          href: signInHref,
          "data-testid": "search-cta-sign-in-button",
          id: "search-cta-sign-in-button",
          "aria-label": ARIA_LABELS.SIGN_IN_JOIN,
          iconLeft: userDataError ? /* @__PURE__ */ jsx(ErrorIcon, {}) : null,
          children: LABELS.SIGN_IN_JOIN
        }
      ),
      /* @__PURE__ */ jsx(SourceIdButton, {})
    ] });
  };
  const MobileAccountButtonState = () => {
    if (isUserDataLoading) {
      return /* @__PURE__ */ jsx(SkeletonSearchCTADesktop, { skeletonMock: [mockSkeletonSearchCTA[0]] });
    }
    if (isAuthenticated) {
      return null;
    }
    return /* @__PURE__ */ jsx(
      SignInButton,
      {
        sx: { padding: "13.5px 16px" },
        style: "outlined",
        color: "blue",
        href: signInHref,
        "data-testid": "search-cta-mobile-sign-in-button",
        id: "search-cta-mobile-sign-in-button",
        "aria-label": ARIA_LABELS.SIGN_IN_JOIN,
        iconLeft: userDataError ? /* @__PURE__ */ jsx(ErrorIcon, {}) : null,
        children: LABELS.SIGN_IN_JOIN
      }
    );
  };
  const handleMobileSearchBarClick = () => {
    const isNewStatusOpen = !isSearchBarOpen;
    const handleTriggerBehavior = isNewStatusOpen ? onOpenSearchBar : onCloseSearchBar;
    handleTriggerBehavior();
    handleSearchBarClick(isNewStatusOpen);
  };
  const MobileSearchCTALayout = () => /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(MobileAccountButtonState, {}),
    /* @__PURE__ */ jsx(SearchIcon, { onClick: handleMobileSearchBarClick, isActive: isSearchBarOpen })
  ] });
  const DesktopSearchCTALayout = () => /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(SearchIcon, { onClick: () => handleSearchBarClick(!isSearchBarOpen), isActive: isSearchBarOpen }),
    /* @__PURE__ */ jsx(DesktopCTAButtonState, {})
  ] });
  return /* @__PURE__ */ jsxs(SearchCTAContainer, { "data-testid": "search-cta-container", role: "navigation", "aria-label": "Search call-to-action", children: [
    /* @__PURE__ */ jsx(
      MegaMenu,
      {
        isVisible: isAuthenticated && isMyAccountMenuOpen,
        navBarProps: myAccountMenuProps,
        navigationLinkNodes: globalAccountLinks,
        ribbonParentRef: ribbonRef,
        isAccountMenu: true,
        userName
      }
    ),
    isDesktop ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(DesktopSearchCTALayout, {}),
      /* @__PURE__ */ jsx(
        SearchBarDesktop,
        {
          isVisible: isSearchBarOpen,
          searchBarProps,
          ribbonParentRef: ribbonRef
        }
      )
    ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(MobileSearchCTALayout, {}),
      /* @__PURE__ */ jsx(
        SearchBarMobile,
        {
          isVisible: isSearchBarOpen,
          searchBarProps,
          ribbonParentRef: ribbonRef
        }
      )
    ] })
  ] });
};
SearchCTA.propTypes = {
  isDesktop: PropTypes.bool.isRequired,
  ribbonRef: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func
  ]),
  onOpenSearchBar: PropTypes.func,
  onCloseSearchBar: PropTypes.func
};
const StyledSkeletonNavBarDesktop = styled(Box)(
  () => {
    return `
        opacity: 0;    
        animation: navSkeletonDelay 100ms linear 333ms forwards;
            @keyframes navSkeletonDelay {
                0% {
                    opacity: 0;
                },
                100% {
                    opacity: 1
                }
            }
        `;
  }
);
const StyledSkeletonMarketplace = styled(StyledSkeletonNavBarDesktop)(({ theme }) => ({
  display: "flex",
  gap: "24px",
  width: "100%",
  flexDirection: "column",
  "& .MuiSkeleton-root": {
    minHeight: "380px",
    [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.large)]: {
      minHeight: "252px"
    }
  }
}));
const SkeletonNavBarDesktop = () => {
  return /* @__PURE__ */ jsx(StyledSkeletonNavBarDesktop, { "data-testid": "skeleton-container", sx: { display: "flex", gap: "24px" }, children: mockSkeletonNavBar.map((option, index) => /* @__PURE__ */ jsx(Skeleton, { ...option }, `SkeletonNavBarDesktop-${index}`)) });
};
const SkeletonMarketplace = ({ sx = {}, totalRows = TOTAL_SKELETON_LOADER_CARDS }) => {
  const baseKey = "skeleton-marketplace";
  return /* @__PURE__ */ jsx(StyledSkeletonMarketplace, { "data-testid": `${baseKey}-container`, sx, children: mockSkeletonMarketplace.slice(0, totalRows).map((option, index) => /* @__PURE__ */ jsx(
    Skeleton,
    {
      "data-testid": `${baseKey}-${index}`,
      ...option
    },
    `${baseKey}-${index}`
  )) });
};
SkeletonMarketplace.propTypes = {
  sx: PropTypes.object,
  totalRows: PropTypes.number
};
const SkeletonSearchCTADesktop = ({ skeletonMock = mockSkeletonSearchCTA }) => {
  return /* @__PURE__ */ jsx(Box, { "data-testid": "skeleton-cta-container", sx: { display: "flex", gap: "16px" }, children: skeletonMock.map((option, index) => /* @__PURE__ */ jsx(Skeleton, { ...option }, `SkeletonSearchCTADesktop-${index}`)) });
};
const StyledDrawer = styled(Drawer)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.extra_large)]: {
    display: "contents"
  },
  "& .MuiPaper-root": {
    boxShadow: theme.vars.shadows[2],
    padding: "0 40px 20px 40px",
    alignItems: "center"
  }
}));
const SearchBarContainer = styled(Box)(
  ({ theme }) => {
    var _a, _b;
    return {
      display: "flex",
      flexDirection: "row",
      maxWidth: "1288px",
      width: "100%",
      padding: "24px 0px",
      "& .MuiBox-root": {
        flexShrink: 1,
        paddingRight: "16px",
        marginBottom: "0px",
        "& .MuiInputBase-root": {
          maxWidth: "100%",
          maxHeight: "48px",
          "& input": {
            width: "100%",
            "&::placeholder": {
              color: (_a = theme.vars.palette.grey) == null ? void 0 : _a[40]
            }
          },
          "& fieldset": {
            border: `1.5px solid ${(_b = theme.vars.palette.grey) == null ? void 0 : _b[50]}`
          }
        }
      },
      "& button": {
        marginLeft: "auto",
        maxHeight: "48px"
      }
    };
  }
);
const StyledButton = styled(Button)(
  ({ theme }) => {
    var _a;
    return {
      cursor: "pointer",
      fontFamily: (_a = theme == null ? void 0 : theme.typography) == null ? void 0 : _a.fontFamily,
      "&:focus-visible": {
        outline: "auto 1px black"
      }
    };
  }
);
const StyledDrawerMobile = styled(Drawer)({
  display: "contents",
  "& .MuiPaper-root": {
    boxShadow: "none",
    alignItems: "center"
  }
});
const SearchBarContainerMobile = styled(Box)(
  ({ theme }) => {
    var _a, _b;
    return {
      width: "100%",
      padding: "24px 16px",
      "& .MuiBox-root": {
        flexShrink: 1,
        paddingRight: "0px",
        marginBottom: "0px",
        "& .MuiInputBase-root": {
          maxWidth: "100%",
          maxHeight: "48px",
          "& input": {
            width: "100%",
            "&::placeholder": {
              color: (_a = theme.vars.palette.grey) == null ? void 0 : _a[40]
            }
          },
          "& fieldset": {
            border: `1.5px solid ${(_b = theme.vars.palette.grey) == null ? void 0 : _b[50]}`
          }
        }
      }
    };
  }
);
const StyledForm = styled("form")({
  display: "contents"
});
const SearchBarDesktop = ({
  isVisible,
  ribbonParentRef = {},
  sx = {},
  searchBarProps = {}
}) => {
  const { handleInputChange, searchTerm, searchUrl } = useSearchBar({ isVisible });
  const {
    paperProps,
    backdropProps,
    slideProps,
    modalProps
  } = getDrawerProps({
    ribbonParentRef,
    dataTestIdPaper: "search-bar-desktop-paper",
    dataTestIdBackdrop: "nav-bar-backdrop"
  });
  return /* @__PURE__ */ jsx(
    StyledDrawer,
    {
      open: isVisible,
      anchor: "top",
      "data-testid": "search-bar-desktop",
      transitionDuration: DRAWER_TRANSITION_DURATION,
      sx,
      ...searchBarProps,
      PaperProps: paperProps,
      BackdropProps: backdropProps,
      SlideProps: slideProps,
      ModalProps: modalProps,
      elevation: 5,
      children: /* @__PURE__ */ jsx(SearchBarContainer, { "data-testid": "search-bar-container", children: /* @__PURE__ */ jsxs(
        StyledForm,
        {
          action: searchUrl,
          "accept-charset": "utf-8",
          method: "get",
          children: [
            /* @__PURE__ */ jsx(
              TextField,
              {
                autoFocus: isVisible,
                "data-testid": "search-bar-input-text",
                icon: Search,
                inputProps: { maxLength: 255 },
                name: "q",
                onInputChange: handleInputChange,
                placeholder: LABELS.SEARCH_PLACEHOLDER,
                value: searchTerm
              }
            ),
            /* @__PURE__ */ jsx(
              StyledButton,
              {
                "data-testid": "search-button",
                style: "outlined",
                color: "blue",
                role: "button",
                type: "submit",
                children: LABELS.SEARCH
              }
            )
          ]
        }
      ) })
    }
  );
};
SearchBarDesktop.propTypes = {
  isVisible: PropTypes.bool,
  ribbonParentRef: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func
  ]),
  sx: PropTypes.object,
  searchBarProps: PropTypes.object
};
const SearchBarMobile = ({
  isVisible,
  ribbonParentRef = {},
  sx = {},
  searchBarProps = {}
}) => {
  const { handleInputChange, searchTerm, searchUrl } = useSearchBar({ isVisible });
  const {
    paperProps,
    backdropProps,
    slideProps,
    modalProps
  } = getDrawerProps({
    ribbonParentRef,
    dataTestIdPaper: "search-bar-mobile-paper",
    dataTestIdBackdrop: "nav-bar-backdrop"
  });
  return /* @__PURE__ */ jsx(
    StyledDrawerMobile,
    {
      open: isVisible,
      anchor: "top",
      "data-testid": "search-bar-mobile",
      transitionDuration: DRAWER_TRANSITION_DURATION,
      sx,
      ...searchBarProps,
      PaperProps: paperProps,
      BackdropProps: backdropProps,
      SlideProps: slideProps,
      ModalProps: modalProps,
      elevation: 5,
      children: /* @__PURE__ */ jsx(SearchBarContainerMobile, { "data-testid": "search-bar-container", children: /* @__PURE__ */ jsx(
        StyledForm,
        {
          action: searchUrl,
          "accept-charset": "utf-8",
          method: "get",
          children: /* @__PURE__ */ jsx(
            TextField,
            {
              autoFocus: isVisible,
              "data-testid": "search-bar-input-text",
              icon: Search,
              inputProps: { maxLength: 255 },
              name: "q",
              onInputChange: handleInputChange,
              placeholder: LABELS.SEARCH_PLACEHOLDER,
              value: searchTerm
            }
          )
        }
      ) })
    }
  );
};
SearchBarMobile.propTypes = {
  isVisible: PropTypes.bool,
  ribbonParentRef: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func
  ]),
  sx: PropTypes.object,
  searchBarProps: PropTypes.object
};
const OfferCardLayout = styled$1(Box)(({ theme }) => {
  var _a, _b;
  return {
    [`#${MKT_CONTAINER_ID} &`]: {
      display: "flex",
      padding: "32px 24px 24px 24px",
      flexDirection: "column",
      alignItems: "center",
      gap: "24px",
      borderRadius: "8px",
      border: `1px solid ${(_a = theme.vars.palette.grey) == null ? void 0 : _a[20]}`,
      background: (_b = theme.vars.palette.grey) == null ? void 0 : _b[0],
      [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.large)]: {
        minWidth: "945px",
        maxWidth: "1312px",
        height: "252px",
        padding: "32px 48px",
        flexDirection: "row"
      },
      [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.extra_extra_large)]: {
        gap: "48px"
      }
    }
  };
});
const CardHeader = styled$1(Box)(({ theme }) => ({
  [`#${MKT_CONTAINER_ID} &`]: {
    "&.MuiBox-root": {
      width: "100%"
    },
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    marginLeft: "auto",
    alignItems: "center",
    gap: "8px",
    "& img": {
      height: "auto",
      width: "186px",
      [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.large)]: {
        width: "210px",
        paddingRight: "24px"
      },
      [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.extra_extra_large)]: {
        width: "234px",
        paddingRight: "48px"
      }
    }
  }
}));
const CardBody = styled$1(Box)(({ theme }) => ({
  [`#${MKT_CONTAINER_ID} &`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    alignSelf: "stretch",
    gap: "20px",
    [theme.breakpoints.up(BREAKPOINT_DIMENSIONS.large)]: {
      flexDirection: "row",
      flexBasis: "0",
      flexGrow: "1",
      alignItems: "center",
      gap: "0px"
    }
  }
}));
function OfferCard({
  sx = {},
  index = 0,
  offer,
  sourceId,
  marketingClickId,
  isTestMode
}) {
  return /* @__PURE__ */ jsxs(
    Box,
    {
      sx,
      "data-testid": `offer-card-${offer.clientId}`,
      itemScope: true,
      itemProp: "item",
      itemType: "https://schema.org/FinancialProduct https://schema.org/LoanOrCredit",
      children: [
        /* @__PURE__ */ jsx("meta", { itemProp: "name", content: offer.clientName }),
        /* @__PURE__ */ jsxs(
          OfferCardLayout,
          {
            itemScope: true,
            itemProp: "provider",
            itemType: "https://schema.org/Organization",
            children: [
              /* @__PURE__ */ jsx("meta", { itemProp: "name", content: offer.clientName }),
              /* @__PURE__ */ jsxs(CardBody, { children: [
                /* @__PURE__ */ jsx(CardHeader, { children: /* @__PURE__ */ jsx(
                  OfferLogo,
                  {
                    logoUrl: offer.logoUrl,
                    clientId: offer.clientId,
                    clientName: offer.clientName
                  }
                ) }),
                /* @__PURE__ */ jsx(ProviderHighlights, { highlights: offer.highlights, clientId: offer.clientId }),
                /* @__PURE__ */ jsx(
                  OfferCriteria,
                  {
                    criteria: offer.criteria,
                    clientId: offer.clientId
                  }
                ),
                /* @__PURE__ */ jsx(
                  OfferCta,
                  {
                    clientId: offer.clientId,
                    clientName: offer.clientName,
                    ctaButtonText: offer.ctaButtonText,
                    ctaUrl: offer.ctaUrl,
                    ctaAdditionalText: offer.ctaAdditionalText,
                    sourceId,
                    marketingClickId,
                    isTestMode
                  }
                )
              ] })
            ]
          }
        )
      ]
    }
  );
}
OfferCard.propTypes = {
  sx: PropTypes.object,
  index: PropTypes.number,
  offer: PropTypes.shape({
    clientId: PropTypes.string.isRequired,
    clientName: PropTypes.string.isRequired,
    logoUrl: PropTypes.string.isRequired,
    trustPilotUrl: PropTypes.oneOfType([() => null, PropTypes.string]),
    trustPilotBusinessId: PropTypes.oneOfType([() => null, PropTypes.string]),
    ctaButtonText: PropTypes.string.isRequired,
    ctaUrl: PropTypes.string.isRequired,
    ctaAdditionalText: PropTypes.string,
    highlights: PropTypes.arrayOf(PropTypes.string).isRequired,
    criteria: PropTypes.arrayOf(PropTypes.object).isRequired
  }).isRequired,
  sourceId: PropTypes.string.isRequired,
  marketingClickId: PropTypes.string.isRequired,
  isTestMode: PropTypes.bool
};
export {
  MobileRibbonLayout as M,
  NavigationBarDesktop as N,
  OfferCard as O,
  StyledRibbonContainer as S,
  StyledDesktopAppBar as a,
  StyledContainer as b,
  SearchCTA as c,
  MegaMenu as d,
  NavigationBarMobile as e,
  SearchBarDesktop as f,
  SearchBarMobile as g,
  SkeletonMarketplace as h,
  SkeletonNavBarDesktop as i,
  SkeletonSearchCTADesktop as j
};
//# sourceMappingURL=OfferCard-C6oPasBm.js.map
